import React, {useMemo, useState} from "react";
import {NodeType} from "../../../model/ModelData";
import {HugeiconsIcon} from "@hugeicons/react";
import {
    // glavni jazli
    KeyboardIcon,
    Message01Icon,
    MagicWand01Icon,
    BrainCircuitIcon,
    AiBrain03Icon,
    PhoneForwardedIcon,
    ApiIcon,
    HierarchySquare01Icon,
    Logout01Icon,
    // Input submenu
    InputShortTextIcon,
    BubbleChatQuestionIcon,
    CheckListIcon,
    // Output submenu
    TextIcon,
    JavaScriptIcon,
    // Logic submenu
    GitForkIcon,
    VariableIcon,
    // AI submenu
    AiUserIcon,
    ChatBotIcon,
    AiNetworkIcon,
    HelpCircleIcon,
    // Connector submenu
    PlugSocketIcon,
    Mail01Icon,
    // strelki
    ArrowDown01Icon,
    ArrowRight01Icon,
    ArrowUp01Icon,
} from "@hugeicons/core-free-icons";
import {Box, Button, Fade, Menu, MenuItem, Stack, Tooltip, Typography} from "@mui/material";
import {Debugger} from "./debugger/Debugger";
import {hexToRgba, NODE_SHADOW, NODE_SHADOW_HOVER} from "../../../nodes/nodeTheme";

const ACCENT = "#6366F1";
const INK = "#1E293B";
const MUTED = "#64748B";

// Eden renderer za site ikoni, za golemina i debelina da se menuvaat na edno mesto
const hi = (icon, size = 22, props = {}) => (
    <HugeiconsIcon icon={icon} size={size} strokeWidth={1.5} color="currentColor" {...props}/>
);

export const NodeMenu = ({addNode, openChat, updatePosition, useLLM, onToggleLLM}) => {
    const [showMore, setShowMore] = useState(true);
    const nodeItems = useMemo(() => {
        return [
            {
                type: "input",
                label: "Input",
                icon: hi(KeyboardIcon),
                items: [
                    {type: NodeType.INPUT, label: "Text", icon: hi(InputShortTextIcon, 20)},
                    {type: NodeType.QUERY, label: "Query", icon: hi(BubbleChatQuestionIcon, 20)},
                    {type: NodeType.CHOICE, label: "Choice", icon: hi(CheckListIcon, 20)},
                ]
            },
            {
                type: "output",
                label: "Output",
                icon: hi(Message01Icon),
                items: [
                    {type: NodeType.TEXT, label: "Text", icon: hi(TextIcon, 20)},
                    {type: NodeType.JAVASCRIPT, label: "Javascript", icon: hi(JavaScriptIcon, 20)},
                ]
            },
            {
                type: "use_llm",
                label: <>LLM<br/>Booster</>,
                title: "LLM Booster",
                icon: hi(MagicWand01Icon, 20)
            },
            {
                type: "logic",
                label: "Logic",
                icon: hi(BrainCircuitIcon),
                items: [
                    {type: NodeType.SWITCH, label: "Switch", icon: hi(GitForkIcon, 20)},
                    {type: NodeType.SET_VAR, label: "Set", icon: hi(VariableIcon, 20)},
                ]
            },
            {
                type: "ai",
                label: "AI",
                icon: hi(AiBrain03Icon),
                items: [
                    {type: NodeType.AGENT, label: "Agent", icon: hi(AiUserIcon, 20)},
                    {type: NodeType.CHATBOT, label: "Chatbot", icon: hi(ChatBotIcon, 20)},
                    {type: NodeType.GEN_AI_WORKFLOW, label: "GenAI Workflow", icon: hi(AiNetworkIcon, 20)},
                    {type: NodeType.QA, label: "QA", icon: hi(HelpCircleIcon, 20)},
                ]
            },
            {
                type: "connector",
                label: "Connector",
                icon: hi(PlugSocketIcon),
                items: [
                    {type: NodeType.MAIL_CONNECTOR, label: "Mail Connector", icon: hi(Mail01Icon, 20)},
                ]
            },
            {type: NodeType.CALL_TRANSFER, label: "Transfer", icon: hi(PhoneForwardedIcon)},
            {type: NodeType.HOOK, label: "Hook", icon: hi(ApiIcon)},
            {type: NodeType.START_FLOW, label: "Flow", icon: hi(HierarchySquare01Icon)},
            {type: NodeType.END, label: "End", icon: hi(Logout01Icon)},
        ]
    }, [])

    if (openChat) {
        return <Debugger open={openChat} updatePosition={updatePosition}/>;
    }

    const visibleItems = nodeItems.slice(0, showMore ? nodeItems.length : 6);

    return (
        <Box
            data-tour="flow-node-menu"
            sx={{
                position: "absolute",
                top: 12,
                left: 12,
                bgcolor: "#FFFFFF",
                border: `1px solid ${hexToRgba(ACCENT, 0.16)}`,
                borderRadius: "14px",
                boxShadow: NODE_SHADOW,
                zIndex: 990,
                p: 0.5,
                width: 58,
            }}
        >
            <Stack direction={"column"} gap={0.25}>
                {visibleItems.map(item => (
                    <NodeMenuItem
                        key={item.type}
                        icon={item.icon}
                        items={item.items || []}
                        title={item.title || ""}
                        type={item.type}
                        useLLM={useLLM}
                        onToggleLLM={onToggleLLM}
                        label={item.label}
                        addNode={addNode}
                    />
                ))}

                <Box sx={{height: "1px", bgcolor: hexToRgba(ACCENT, 0.12), mx: 0.5, my: 0.25}}/>

                <Tooltip placement={"right"} title={showMore ? "Show less" : "Show more"}>
                    <Button
                        disableRipple
                        onClick={() => setShowMore(!showMore)}
                        sx={{
                            minWidth: 0,
                            width: "100%",
                            py: 0.5,
                            borderRadius: "9px",
                            color: MUTED,
                            transition: "background-color .15s ease, color .15s ease",
                            "&:hover": {bgcolor: hexToRgba(ACCENT, 0.08), color: INK},
                        }}
                    >
                        {showMore ? hi(ArrowUp01Icon, 18) : hi(ArrowDown01Icon, 18)}
                    </Button>
                </Tooltip>
            </Stack>
        </Box>
    );
}

const NodeMenuItem = ({type, label, title, icon, items = [], useLLM, addNode, onToggleLLM}) => {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);
    const hasChildren = items.length > 0;

    const handleClick = (event) => {
        if (hasChildren) {
            setAnchorEl(event.currentTarget);
        } else if (type === "use_llm") {
            onToggleLLM();
        } else {
            addNode(type);
        }
    };
    const handleClose = (selectedType) => {
        if (Object.values(NodeType)?.includes(selectedType)) {
            addNode(selectedType);
        }
        if (selectedType === "use_llm") {
            onToggleLLM();
        }
        setAnchorEl(null);
    };

    const active = (type === "use_llm" && useLLM) || open;

    return (
        <>
            <Fade in timeout={250}>
                <Tooltip placement={"right"} title={`Create ${title || label} node`}>
                    <Button
                        disableRipple
                        id="fade-button"
                        aria-controls={open ? "fade-menu" : undefined}
                        aria-haspopup="true"
                        aria-expanded={open ? "true" : undefined}
                        onClick={handleClick}
                        sx={{
                            position: "relative",
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.25,
                            alignItems: "center",
                            justifyContent: "center",
                            textTransform: "none",
                            minWidth: 0,
                            width: "100%",
                            py: 0.5,
                            px: 0,
                            borderRadius: "9px",
                            color: active ? ACCENT : MUTED,
                            bgcolor: active ? hexToRgba(ACCENT, 0.1) : "transparent",
                            transition: "background-color .15s ease, color .15s ease",
                            "& svg": {transition: "color .15s ease, stroke .15s ease"},
                            "&:hover": {
                                bgcolor: hexToRgba(ACCENT, active ? 0.14 : 0.08),
                                color: active ? ACCENT : INK,
                            },
                        }}
                    >
                        {hasChildren && hi(ArrowRight01Icon, 11, {
                            style: {position: "absolute", top: 4, right: 4, opacity: 0.55}
                        })}
                        {icon}
                        <Typography
                            noWrap
                            sx={{
                                fontSize: 9.5,
                                lineHeight: 1.15,
                                fontWeight: active ? 600 : 500,
                                letterSpacing: "0.01em",
                                color: "inherit",
                                textAlign: "center",
                            }}
                        >
                            {label}
                        </Typography>
                    </Button>
                </Tooltip>
            </Fade>

            {hasChildren && open && (
                <Menu
                    id="fade-menu"
                    MenuListProps={{"aria-labelledby": "fade-button", dense: true, sx: {py: 0.5}}}
                    anchorEl={anchorEl}
                    anchorOrigin={{vertical: "top", horizontal: "right"}}
                    transformOrigin={{vertical: "top", horizontal: "left"}}
                    sx={{ml: 1}}
                    slotProps={{
                        paper: {
                            sx: {
                                borderRadius: "12px",
                                border: `1px solid ${hexToRgba(ACCENT, 0.16)}`,
                                boxShadow: NODE_SHADOW_HOVER,
                                px: 0.5,
                            },
                        },
                    }}
                    open={open}
                    onClose={handleClose}
                    TransitionComponent={Fade}
                >
                    {items.map(item => (
                        <MenuItem
                            key={item.type}
                            onClick={() => handleClose(item.type)}
                            sx={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                gap: 1.25,
                                minWidth: 168,
                                borderRadius: "8px",
                                py: 0.5,
                                my: 0.15,
                                color: MUTED,
                                // Hugeicons crtaat so currentColor, pa e dovolno da se postavi color
                                "& svg": {color: ACCENT},
                                "&:hover": {bgcolor: hexToRgba(ACCENT, 0.08), color: INK},
                            }}
                        >
                            {item.icon}
                            <Typography noWrap sx={{fontSize: 12.5, fontWeight: 500, color: "inherit"}}>
                                {item.label}
                            </Typography>
                        </MenuItem>
                    ))}
                </Menu>
            )}
        </>
    )
}
